import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { isBackSwipe, nativeBackDestination } from '../lib/nativeNavigation';

/** Native iOS WebViews do not reliably translate a history swipe into a React Router navigation. */
export function NativeBackGesture() {
  const location = useLocation();
  const navigate = useNavigate();
  const start = useRef<{ x: number; y: number; id: number; destination: string; dialog: HTMLDialogElement | null } | null>(null);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;
    const destination = nativeBackDestination(location.pathname, location.search, location.state);
    if (!destination || location.pathname === '/app/discovery-map-frame') return;

    function onStart(event: TouchEvent) {
      start.current = null;
      if (event.touches.length !== 1 || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"], [data-native-back-swipe="off"]')) return;
      const touch = event.touches[0];
      if (touch.clientX > 28) return;
      const preferred = target instanceof Element ? target.closest('[data-native-back-destination]')?.getAttribute('data-native-back-destination') : null;
      const openDialogs = document.querySelectorAll<HTMLDialogElement>('dialog[open]');
      start.current = { x: touch.clientX, y: touch.clientY, id: touch.identifier, destination: preferred?.startsWith('/app/') ? preferred : destination!, dialog: openDialogs.item(openDialogs.length - 1) };
    }

    function onEnd(event: TouchEvent) {
      const initial = start.current;
      start.current = null;
      if (!initial || event.changedTouches.length !== 1 || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const touch = event.changedTouches[0];
      if (touch.identifier !== initial.id) return;
      if (!isBackSwipe(initial.x, initial.y, touch.clientX, touch.clientY)) return;
      if (initial.dialog?.open) {
        const dialog = initial.dialog as HTMLDialogElement & { requestClose?: () => void };
        if (typeof dialog.requestClose === 'function') dialog.requestClose();
        else if (dialog.dispatchEvent(new Event('cancel', { cancelable: true }))) dialog.close();
        return;
      }
      navigate(initial.destination);
    }

    function onCancel() { start.current = null; }
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
    };
  }, [location.pathname, location.search, location.state, navigate]);

  return null;
}
